import axios from 'axios';

// BE chạy ở 5001 (5000 bị macOS AirPlay chiếm). Có thể override bằng REACT_APP_API_URL.
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// JWT token đăng nhập admin (lưu localStorage). FE KHÔNG còn chứa secret nào.
const TOKEN_KEY = 'admin_token';
export const getToken = (): string => localStorage.getItem(TOKEN_KEY) || '';
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ─── Types ──────────────────────────────────────────
export type ShopType = 'mall' | 'thuong';
export type FeeUnit = '%' | 'VND';

export interface Fee {
  _id: string;
  feeKey: string;
  label: string;
  unit: FeeUnit;
  shopTypes: ShopType[];
  optional: boolean;
  optionalLabel: string;
  capped: boolean;
  maxValue: number;
  valueMall: number;
  valueThuong: number;
  sortOrder: number;
  isActive: boolean;
}

export type FeeInput = Omit<Fee, '_id' | 'feeKey'>;

export interface LoaiSanPham {
  tenLoai: string;
  phiHoaHong: string;
  loaiCon?: { tenLoai: string; phiHoaHong: string }[];
}

export interface NganhHang {
  _id: string;
  tenNganh: string;
  loaiSanPham: LoaiSanPham[];
  loaiShop: 'mall' | 'regular';
  quocGia: string;
  isActive: boolean;
}

export interface TuVan {
  id: string;
  fullName: string;
  phoneNumber: string;
  shopIssue: string;
  revenueScale: string;
  shopLink: string;
  message?: string;
  prizeWon?: string;
  source?: string;
  trangThai: 'moi' | 'dang_xu_ly' | 'da_xu_ly';
  createdAt: string;
}

export interface Ebook {
  _id: string;
  slug: string;
  title: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  imageUrl?: string;
  driveLink?: string;
  driveLinkEn?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ServiceMenuItem {
  label: string;
  url: string;
}

export interface AppConfig {
  dataVersion: string;
  consultationServices: string[];
  zaloGroupLink: string;
  shopeeServices: ServiceMenuItem[];
  consultationImage: string;
}

export interface Footer {
  companyName: string;
  taxNumber: string;
  issueDate: string;
  issuer: string;
  representative: string;
  addresses: string[];
  phone: string;
  email: string;
  websiteLabel: string;
  websiteUrl: string;
  facebookUrl: string;
  zaloUrl: string;
  youtubeUrl: string;
  hotline: string;
  copyright: string;
  description: string;
}

// ─── Client ─────────────────────────────────────────
const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

async function request<T>(
  endpoint: string,
  options: { method?: string; body?: unknown } = {}
): Promise<ApiResponse<T>> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await instance({ url: endpoint, method, data: options.body, headers });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // Token hết hạn/không hợp lệ → xoá token + báo cho AuthContext logout
      if (error.response.status === 401) {
        clearToken();
        window.dispatchEvent(new Event('admin-unauthorized'));
      }
      const message =
        error.response.data?.message || error.response.data?.error || error.message;
      throw new Error(message);
    }
    throw error;
  }
}

export const adminApi = {
  // ── Auth (JWT) ──
  async login(username: string, password: string) {
    const res = await request<{ token: string; user: { username: string; role: string } }>(
      '/api/auth/login',
      { method: 'POST', body: { username, password } }
    );
    if (res.success && res.data?.token) setToken(res.data.token);
    return res;
  },
  me: () => request<{ username: string; role: string }>('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<null>('/api/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),
  /** Đá mọi thiết bị khác. BE trả token mới → lưu lại để máy hiện tại KHÔNG bị đăng xuất. */
  async logoutAllDevices() {
    const res = await request<{ token: string }>('/api/auth/logout-all', { method: 'POST' });
    if (res.success && res.data?.token) setToken(res.data.token);
    return res;
  },
  logout: () => clearToken(),

  // ── Chi phí (phí tính giá VN) ──
  getChiPhi: () => request<Fee[]>('/api/chi-phi'),
  createFee: (body: FeeInput) => request<Fee>('/api/chi-phi', { method: 'POST', body }),
  updateFee: (id: string, body: FeeInput) => request<Fee>(`/api/chi-phi/${id}`, { method: 'PUT', body }),
  deleteFee: (id: string) => request<null>(`/api/chi-phi/${id}`, { method: 'DELETE' }),

  // ── Ngành hàng (phí cố định VN) ──
  getNganhHang: (loaiShop?: 'mall' | 'regular') =>
    request<NganhHang[]>(`/api/nganh-hang${loaiShop ? `?loaiShop=${loaiShop}` : ''}`),
  createNganhHang: (body: { tenNganh: string; loaiSanPham: LoaiSanPham[]; loaiShop: 'mall' | 'regular' }) =>
    request<NganhHang>('/api/nganh-hang', { method: 'POST', body }),
  updateNganhHang: (id: string, body: Partial<{ tenNganh: string; loaiSanPham: LoaiSanPham[] }>) =>
    request<NganhHang>(`/api/nganh-hang/${id}`, { method: 'PUT', body }),
  deleteNganhHang: (id: string) =>
    request<null>(`/api/nganh-hang/${id}`, { method: 'DELETE' }),
  importNganhHang: (data: unknown[], opts?: { clearExisting?: boolean; loaiShop?: 'mall' | 'regular' }) =>
    request<unknown>('/api/nganh-hang/import-json', { method: 'POST', body: { data, ...opts } }),
  async uploadNganhHangPdf(
    file: File,
    opts: { clearExisting?: boolean; loaiShop: 'mall' | 'regular' }
  ): Promise<{ success: boolean; message?: string; totalParsed?: number; skippedRows?: number; errors?: string[] }> {
    const form = new FormData();
    form.append('file', file);
    form.append('loaiShop', opts.loaiShop);
    if (opts.clearExisting) form.append('clearExisting', 'true');
    // axios global để browser tự set multipart boundary; validateStatus để lấy message lỗi 400 từ BE.
    const res = await axios.post(`${API_BASE_URL}/api/nganh-hang/upload-pdf`, form, {
      headers: { Authorization: `Bearer ${getToken()}` },
      validateStatus: () => true,
    });
    return res.data;
  },

  // ── Tư vấn ──
  getTuVan: () => request<TuVan[]>('/api/tu-van'),
  updateTuVanStatus: (id: string, trangThai: TuVan['trangThai']) =>
    request<TuVan>(`/api/tu-van/${id}/trang-thai`, { method: 'PATCH', body: { trangThai } }),
  deleteTuVan: (id: string) => request<null>(`/api/tu-van/${id}`, { method: 'DELETE' }),

  // ── Ebook ──
  getEbooks: () => request<Ebook[]>('/api/ebook/all'),
  createEbook: (body: Partial<Ebook>) => request<Ebook>('/api/ebook', { method: 'POST', body }),
  updateEbook: (id: string, body: Partial<Ebook>) => request<Ebook>(`/api/ebook/${id}`, { method: 'PUT', body }),
  deleteEbook: (id: string) => request<null>(`/api/ebook/${id}`, { method: 'DELETE' }),
  async uploadEbookImage(file: File): Promise<string> {
    const form = new FormData();
    form.append('file', file);
    // Dùng axios global (KHÔNG dùng instance) để tránh default Content-Type: application/json.
    // Với FormData, trình duyệt tự set multipart/form-data kèm boundary.
    const res = await axios.post(`${API_BASE_URL}/api/ebook/upload-image`, form, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.data?.success) throw new Error(res.data?.message || 'Upload ảnh thất bại');
    return res.data.data.url as string;
  },

  // ── Cấu hình (settings) ──
  getConfig: () => request<AppConfig>('/api/config'),
  getFooter: () => request<Footer>('/api/config/footer'),
  updateConfig: (body: Partial<{
    consultationServices: string[];
    zaloGroupLink: string;
    shopeeServices: ServiceMenuItem[];
    consultationImage: string;
    footer: Footer;
  }>) => request<AppConfig>('/api/config', { method: 'PUT', body }),
  bumpDataVersion: () => request<{ dataVersion: string }>('/api/config/version', { method: 'POST' }),
};
